'use client';

import { Check, X } from 'lucide-react';

import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { passwordRequirements } from '../schemas/password.schema';

/**
 * Live password requirements checklist.
 *
 * Renders one row per rule in `passwordRequirements`, updating check/cross as
 * the user types. Driving it from the same array the zod schema validates
 * against guarantees the UI can never promise a rule the server doesn't enforce
 * (or vice-versa). Unmet rules read muted, met rules turn green — no aggressive
 * red while typing (matches the Wise / Adobe / Relevance AI signup patterns).
 */
export function PasswordRequirementsChecklist({
  password,
  className,
}: {
  password: string;
  className?: string;
}) {
  return (
    <ul
      className={cn('flex flex-col gap-1.5', className)}
      aria-live="polite"
      data-test="password-requirements"
    >
      {passwordRequirements.map((requirement) => {
        const met = requirement.isMet(password);

        return (
          <li
            key={requirement.id}
            className="flex items-center gap-2 text-xs"
            data-requirement={requirement.id}
            data-met={met}
          >
            <span
              className={cn(
                'flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors',
                met
                  ? 'bg-green-600/15 text-green-600 dark:text-green-500'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {met ? (
                <Check className="h-3 w-3" aria-hidden />
              ) : (
                <X className="h-3 w-3" aria-hidden />
              )}
            </span>

            <span
              className={cn(met ? 'text-foreground' : 'text-muted-foreground')}
            >
              <Trans
                i18nKey={requirement.i18nKey}
                values={requirement.i18nValues}
              />
            </span>
          </li>
        );
      })}
    </ul>
  );
}
